import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Star, Clock, Trophy, Info } from "lucide-react";
import "./RaceCard.css";
import CryptoIconWrapper from "./cryptoicons/CryptoIconWrapper";

const RaceCard = ({
  title,
  amount,
  timeLeft: initialTimeLeft,
  poolData,
  onPlaceBet,
}) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [timeLeft, setTimeLeft] = useState(initialTimeLeft);
  const [showTooltip, setShowTooltip] = useState("");

  useEffect(() => {
    // Set up countdown timer
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        const totalSeconds =
          prev.hours * 3600 + prev.minutes * 60 + prev.seconds - 1;
        if (totalSeconds <= 0) {
          clearInterval(timer);
          return { hours: 0, minutes: 0, seconds: 0 };
        }
        return {
          hours: Math.floor(totalSeconds / 3600),
          minutes: Math.floor((totalSeconds % 3600) / 60),
          seconds: totalSeconds % 60,
        };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleFavoriteClick = () => {
    setIsFavorite(!isFavorite);
  };

  const formatNumber = (num) => {
    return num.toString().padStart(2, "0");
  };
  return (
    <div className={`race-card watch-card`}>
      <div className="card-header">
        <div className="title-section">
          <div className="title-status">
            <div className="live-indicator">
              <span className="live-dot"></span>
              Live
            </div>
            <h2>{title}</h2>
          </div>
          <div className="flex gap-3">
            <div
              className={`stats-buttonD action-buttonD  ${isFavorite ? "active" : ""}`}
              onClick={handleFavoriteClick}
              aria-label={
                isFavorite ? "Remove from favorites" : "Add to favorites"
              }
            >
              <Star className={isFavorite ? "favourite-filled" : ""} size={20} />
            </div>
          </div>
        </div>
      </div>

      <div className="info-section">
        <div className="flex">
          <div className="prize-pool flex-grow">
            <span className="prize-pool-label">Total Prize Pool</span>
            <span className="amount">
              ${amount?.toLocaleString() || "0"}
            </span>
          </div>
          <div className="timer-section">
            <div className="timer-label">Starts in</div>
            <div className="horizontal-timer">
              <div className="time-unit">
                <span className="time-value">
                  {formatNumber(timeLeft.hours)}
                </span>
                <span className="time-label">Hrs</span>
              </div>
              <div className="time-unit">
                <span className="time-value">
                  {formatNumber(timeLeft.minutes)}
                </span>
                <span className="time-label">Mins</span>
              </div>
              <div className="time-unit">
                <span className="time-value">
                  {formatNumber(timeLeft.seconds)}
                </span>
                <span className="time-label">Secs</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="divider mt-8"></div>
      <div className="relative overflow-hidden">
        <div className="watch-card-purple-circle"></div>
        <div className="flex p-4 flex-col gap-6 z-50 relative">
          <div className="watch-tokens-grid">
            {poolData.map((token) => (
              <div key={token.symbol} className="token-itemc">
                <div className="token-odds">{token.odds.toFixed(2)}:1</div>
                <div className="">
                  <CryptoIconWrapper name={token.symbol.toLowerCase()} size={50} />
                </div>
                <div className="token-pool">${token.poolSize.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="divider"></div>

      <div className="flex mt-6">
        <div className="flex flex-col flex-grow gap-2">
          <div className="flex gap-2">
            <Clock size={16} />
            <span className="text-sm">Total Prize Pool</span>
            <div className="relative">
              <Info
                size={16}
                className="info-icon"
                onMouseEnter={() => setShowTooltip("prizePool")}
                onMouseLeave={() => setShowTooltip("")}
              />
              {showTooltip === 'prizePool' && (
                <div className="tooltip">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam
                </div>
              )}
            </div>
          </div>
          <div className="race-type">
            <Trophy size={16} />
            <span className="text-sm">Percentage Price Change</span>
            <div className="relative">
              <Info
                size={16}
                className="info-icon"
                onMouseEnter={() => setShowTooltip("priceChange")}
                onMouseLeave={() => setShowTooltip("")}
              />
              {showTooltip === 'priceChange' && (
                <div className="tooltip">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <button
        className="watch-button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onPlaceBet();
        }}
        aria-label="Watch live race"
      >
        Watch Live Race
      </button>

    </div>
  );
};

RaceCard.propTypes = {
  title: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  timeLeft: PropTypes.shape({
    hours: PropTypes.number,
    minutes: PropTypes.number,
    seconds: PropTypes.number,
  }).isRequired,
  poolData: PropTypes.arrayOf(
    PropTypes.shape({
      symbol: PropTypes.string.isRequired,
      odds: PropTypes.number.isRequired,
      poolSize: PropTypes.number.isRequired,
    })
  ).isRequired,
  onPlaceBet: PropTypes.func.isRequired,
};

export default RaceCard;
